import React, { useState, useEffect } from "react";
import { MdOutlineCancelPresentation } from "react-icons/md";
import { createPlan, updatePlan } from "../../services/subscription";
import toast from "react-hot-toast";

function AddPlanModal({ setIsModalOpen, onSuccess, editData }) {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        planName: "",
        amount: "",
        currency: "NGN",
        frequency: "MONTHLY",
        maxPayments: 12,
        description: "",
        status: "ACTIVE",
    });

    useEffect(() => {
        if (editData) {
            setFormData({
                planName: editData.planName || "",
                amount: editData.amount || "",
                currency: editData.currency || "NGN",
                frequency: editData.frequency || "MONTHLY",
                maxPayments: editData.maxPayments || 12,
                description: editData.description || "",
                status: editData.status || "ACTIVE",
            });
        }
    }, [editData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: name === "amount" || name === "maxPayments" ? Number(value) : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (editData) {
                // For PUT, the payload matches the specific fields requested
                const putPayload = {
                    planName: formData.planName,
                    amount: formData.amount,
                    description: formData.description,
                    status: formData.status,
                    maxPayments: formData.maxPayments
                };
                await updatePlan(editData.id, putPayload);
                toast.success("Plan updated successfully");
            } else {
                // Map status to initialStatus for POST if necessary, or just use status if API supports it
                // Based on previous payload example, it was "initialStatus" for POST
                const postPayload = {
                    ...formData,
                    initialStatus: formData.status
                };
                delete postPayload.status;
                await createPlan(postPayload);
                toast.success("Plan created successfully");
            }
            onSuccess();
            setIsModalOpen(false);
        } catch (error) {
            if (error.response) {
                toast.error(error.response.data.message || `Failed to ${editData ? 'update' : 'create'} plan`);
            } else {
                toast.error(`An error occurred while ${editData ? 'updating' : 'creating'} the plan`);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex justify-center items-center">
            <div className="modal-content w-[500px] bg-white p-8 rounded-[20px] shadow-2xl relative h-fit max-h-[95vh] overflow-y-auto">
                <MdOutlineCancelPresentation
                    onClick={() => setIsModalOpen(false)}
                    className="text-primary text-[28px] right-6 absolute top-6 cursor-pointer hover:text-red-500 transition-colors"
                />

                <h3 className="text-2xl text-navy-blue text-left font-bold mb-8 border-b pb-4">
                    {editData ? 'Edit Plan' : 'Create New Plan'}
                </h3>

                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-semibold text-gray-700">Plan Name</label>
                        <input
                            required
                            name="planName"
                            value={formData.planName}
                            onChange={handleChange}
                            className="border border-gray-300 rounded-lg outline-none p-3 w-full focus:border-primary transition-colors"
                            placeholder="e.g. Premium Monthly"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-semibold text-gray-700">Amount</label>
                        <input
                            required
                            name="amount"
                            type="number"
                            value={formData.amount}
                            onChange={handleChange}
                            className="border border-gray-300 rounded-lg outline-none p-3 w-full focus:border-primary transition-colors"
                            placeholder="0.00"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-semibold text-gray-700">Frequency</label>
                            <select
                                name="frequency"
                                value={formData.frequency}
                                onChange={handleChange}
                                disabled={!!editData} // Freq usually shouldn't change after creation
                                className="border border-gray-300 rounded-lg outline-none p-3 w-full focus:border-primary transition-colors bg-white disabled:bg-gray-50"
                            >
                                <option value="DAILY">Daily</option>
                                <option value="WEEKLY">Weekly</option>
                                <option value="MONTHLY">Monthly</option>
                                <option value="YEARLY">Yearly</option>
                            </select>
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-semibold text-gray-700">Max Payments</label>
                            <input
                                required
                                name="maxPayments"
                                type="number"
                                value={formData.maxPayments}
                                onChange={handleChange}
                                className="border border-gray-300 rounded-lg outline-none p-3 w-full focus:border-primary transition-colors"
                                placeholder="e.g. 12"
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-semibold text-gray-700">Description</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            className="border border-gray-300 rounded-lg outline-none p-3 w-full focus:border-primary transition-colors h-24 resize-none"
                            placeholder="Describe the plan features..."
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-semibold text-gray-700">Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="border border-gray-300 rounded-lg outline-none p-3 w-full focus:border-primary transition-colors bg-white"
                        >
                            <option value="ACTIVE">Active</option>
                            <option value="INACTIVE">Inactive</option>
                        </select>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="text-white bg-primary mt-4 w-full h-[50px] rounded-xl font-bold hover:bg-opacity-90 transition-all disabled:bg-gray-400"
                    >
                        {loading ? "Processing..." : (editData ? 'Save Changes' : 'Create Plan')}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default AddPlanModal;
